import { createSlice } from '@reduxjs/toolkit';
import {
    fetchProjectRepositories,
    fetchProjectQuests,
    fetchProjectPlanningSessions,
    fetchProjectWorkTimeReview,
    selectProject,
    SLICE_NAME
} from './service';
import { IProjectBudgetReview } from 'src/types/projects';
import { ClientQuest, Repository } from 'src/types/client';
import { PlanningSession } from 'src/types/planning';
import {
    QUESTS_INITIAL_ROWS_PER_PAGE,
    CURRENT_WORK_QUEST_STATUS
} from 'src/constants/quest';

const PLANNING_SESSIONS_INITIAL_ROWS_PER_PAGE = 10;

const PROJECT_STORAGE_KEY = 'selectedProjectId';

const persistProjectId = (projectId: string | null) => {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        if (projectId) {
            localStorage.setItem(PROJECT_STORAGE_KEY, projectId);
        } else {
            localStorage.removeItem(PROJECT_STORAGE_KEY);
        }
    } catch {
        // ignore persistence errors
    }
};

export interface ClientTypes {
    selectedProjectId: string | null;
    isLoading: boolean;
    repositories: Repository[] | null;
    quests: ClientQuest[] | null;
    totalQuests: number | null;
    workTimeReview: IProjectBudgetReview[] | null;
    questsRowsPerPage: number;
    questsSearchQuery: string;
    questsStatusFilter: number[];
    questsCurrentPage: number;
    onlyCurrentWork: boolean;
    planningSessions: PlanningSession[] | null;
    totalPlanningSessions: number | null;
    planningSessionsRowsPerPage: number;
    planningSessionsCurrentPage: number;
    planningSessionsSearchQuery: string;
}

const initialState: ClientTypes = {
    selectedProjectId: null,
    isLoading: false,
    repositories: null,
    quests: null,
    totalQuests: null,
    workTimeReview: null,
    questsRowsPerPage: QUESTS_INITIAL_ROWS_PER_PAGE,
    questsSearchQuery: '',
    questsStatusFilter: CURRENT_WORK_QUEST_STATUS, // Initialize to match onlyCurrentWork: true - prevents duplicate API calls on load
    questsCurrentPage: 0,
    onlyCurrentWork: true,
    planningSessions: null,
    totalPlanningSessions: null,
    planningSessionsRowsPerPage: PLANNING_SESSIONS_INITIAL_ROWS_PER_PAGE,
    planningSessionsCurrentPage: 0,
    planningSessionsSearchQuery: ''
};

export const ProjectsSlice = createSlice({
    name: SLICE_NAME,
    initialState,
    reducers: {
        setQuestsRowsPerPage: (state, action) => {
            state.questsRowsPerPage = action.payload;
        },
        setQuestsSearchQuery: (state, action) => {
            state.questsSearchQuery = action.payload;
        },
        setQuestsStatusFilter: (state, action) => {
            state.questsStatusFilter = action.payload;
        },
        setQuestsCurrentPage: (state, action) => {
            state.questsCurrentPage = action.payload;
        },
        setOnlyCurrentWork: (state, action) => {
            state.onlyCurrentWork = action.payload;
        },
        setPlanningSessionsRowsPerPage: (state, action) => {
            state.planningSessionsRowsPerPage = action.payload;
        },
        setPlanningSessionsCurrentPage: (state, action) => {
            state.planningSessionsCurrentPage = action.payload;
        },
        setPlanningSessionsSearchQuery: (state, action) => {
            state.planningSessionsSearchQuery = action.payload;
            state.planningSessionsCurrentPage = 0;
        }
    },

    extraReducers: {
        [selectProject.fulfilled.type]: (state, { payload }) => {
            const isNewProject = state.selectedProjectId !== payload;
            state.selectedProjectId = payload;
            if (isNewProject) {
                state.questsCurrentPage = 0;
                state.questsSearchQuery = '';
            }
            state.planningSessionsCurrentPage = 0;
            state.planningSessionsSearchQuery = '';
            persistProjectId(payload);
        },
        [fetchProjectRepositories.pending.type]: (state) => {
            state.isLoading = true;
        },
        [fetchProjectRepositories.fulfilled.type]: (state, { payload }) => {
            state.isLoading = false;
            state.repositories = payload;
        },
        [fetchProjectRepositories.rejected.type]: (state) => {
            state.isLoading = false;
        },
        [fetchProjectQuests.fulfilled.type]: (state, action) => {
            state.totalQuests = action.payload?.totalCount || 0;
            state.questsCurrentPage = action.meta.arg.page;
            state.quests = action.payload?.quests || [];
        },
        [fetchProjectPlanningSessions.fulfilled.type]: (state, action) => {
            state.totalPlanningSessions = action.payload?.totalCount || 0;
            state.planningSessionsCurrentPage = action.meta.arg.page;
            state.planningSessions = action.payload?.sessions || [];
        },
        [fetchProjectWorkTimeReview.fulfilled.type]: (state, { payload }) => {
            state.workTimeReview = payload;
        }
    }
});

export const {
    setQuestsRowsPerPage,
    setQuestsSearchQuery,
    setQuestsStatusFilter,
    setQuestsCurrentPage,
    setOnlyCurrentWork,
    setPlanningSessionsRowsPerPage,
    setPlanningSessionsCurrentPage,
    setPlanningSessionsSearchQuery
} = ProjectsSlice.actions;

export default ProjectsSlice.reducer;
